import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";

import PageHeader from "../../components/ui/PageHeader";

/**
 * Omborlar.
 *
 * Ombor ro'yxati 1C dagi qoldiqlardan o'zi to'ldiriladi -- yangi ombor paydo
 * bo'lsa shu yerda ko'rinadi. Bu yerdan yashirilgan ombor botda ham,
 * hisobotlarda ham chiqmaydi.
 *
 * Ilgari bitta ombor kodga qattiq yozib yashirilgan edi va uni o'zgartirish
 * uchun dasturni qayta chiqarish kerak bo'lardi.
 */

const API = process.env.REACT_APP_API;
const auth = () => ({ headers: { Authorization: localStorage.getItem("token") } });

const WarehousesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // 1C dagi mijoz bo'linmalari (clientsource) va har biridagi mijozlar soni.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/warehouse`, auth());
      setRows(data);
      setError(null);
    } catch {
      setError("Omborlar ro'yxatini olib bo'lmadi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (id, params) => {
    setSavingId(id);
    try {
      const { data } = await axios.patch(
        `${API}/warehouse/${id}?${new URLSearchParams(params)}`,
        null,
        auth()
      );
      setRows((prev) => prev.map((w) => (w.id === id ? data : w)));
      setError(null);
    } catch (err) {
      setError(
        err.response?.status === 403
          ? "Omborni o'zgartirish uchun huquqingiz yo'q."
          : "Saqlab bo'lmadi."
      );
    } finally {
      setSavingId(null);
    }
  };

  const visible = rows.filter((w) => !w.hidden).length;

  return (
    <Box>
      <PageHeader
        title="Omborlar"
        subtitle={`${visible} ta ombor botda ko'rinadi, ${rows.length - visible} tasi yashirilgan.`}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Ro'yxat 1C dagi qoldiqlardan o'zi to'ldiriladi. Yashirilgan ombor botdagi qoldiq
        ro'yxatida ham, umumiy summada ham qatnashmaydi. Agent ilovada qaysi 1C
        mijozlarini ko'rishi Foydalanuvchilar sahifasida belgilanadi: 1C tashkiloti
        va biznes-regioni (tahrirlash oynasi) hamda qarzdorlar uchun omborlar/do'konlar.
      </Alert>

      <Paper sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 6, display: "grid", placeItems: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography variant="h4">Ombor yo'q</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ro'yxat 1C dan qoldiqlar kelgandan keyin to'ladi. Bu har soatda avtomatik
              bajariladi.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>1C dagi nomi</TableCell>
                  <TableCell>Ko'rsatiladigan nom</TableCell>
                  <TableCell align="center" width={110}>
                    Tartib
                  </TableCell>
                  <TableCell align="right" width={150}>
                    Botda ko'rinadi
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((w) => (
                  <TableRow key={w.id} hover sx={{ opacity: w.hidden ? 0.55 : 1 }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {w.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder={w.name}
                        defaultValue={w.displayName || ""}
                        variant="standard"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: "0.875rem" } }}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (w.displayName || "")) patch(w.id, { displayName: v });
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        defaultValue={w.sortOrder}
                        variant="standard"
                        inputProps={{ style: { textAlign: "center", fontFamily: "'JetBrains Mono', monospace" } }}
                        InputProps={{ disableUnderline: true, sx: { fontSize: "0.8125rem", width: 64 } }}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isNaN(v) && v !== w.sortOrder) patch(w.id, { sortOrder: v });
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                        {w.hidden && <Chip size="small" label="Yashirin" variant="outlined" />}
                        <Tooltip title={w.hidden ? "Botda ko'rsatish" : "Botdan yashirish"}>
                          <span>
                            <Switch
                              checked={!w.hidden}
                              disabled={savingId === w.id}
                              onChange={(e) => patch(w.id, { hidden: !e.target.checked })}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default WarehousesPage;
